import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Plus, Pencil, Trash2, Hotel, RefreshCw } from "lucide-react";
import { toast } from "sonner";

export default function HotelRooms() {
  const [rooms, setRooms] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingRoom, setEditingRoom] = useState(null);
  const [formData, setFormData] = useState({
    room_number: "",
    category_id: "",
    floor: "Ground",
    bed_type: "Double",
    smoking_allowed: false,
    status: "Available"
  });

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    setLoading(true);
    try {
      const [roomsRes, catsRes] = await Promise.all([
        api.get("/hotel/rooms"),
        api.get("/hotel/categories")
      ]);
      setRooms(Array.isArray(roomsRes.data) ? roomsRes.data : []);
      setCategories(catsRes.data.categories || []);
    } catch (error) {
      toast.error("Failed to load data");
      console.error(error);
    } finally {
      setLoading(false);
    }
  };

  const openAddDialog = () => {
    setEditingRoom(null);
    setFormData({
      room_number: "",
      category_id: "",
      floor: "Ground",
      bed_type: "Double",
      smoking_allowed: false,
      status: "Available"
    });
    setDialogOpen(true);
  };

  const openEditDialog = (room) => {
    setEditingRoom(room);
    setFormData({
      room_number: room.room_number,
      category_id: room.category_id,
      floor: room.floor,
      bed_type: room.bed_type,
      smoking_allowed: room.smoking_allowed,
      status: room.status
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (editingRoom) {
        await api.put(`/hotel/rooms/${editingRoom.id}`, formData);
        toast.success("Room updated successfully");
      } else {
        await api.post("/hotel/rooms", formData);
        toast.success("Room created successfully");
      }
      setDialogOpen(false);
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Operation failed");
    }
  };

  const handleDelete = async (id) => {
    if (!confirm("Are you sure you want to delete this room?")) return;
    
    try {
      await api.delete(`/hotel/rooms/${id}`);
      toast.success("Room deleted successfully");
      loadData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Delete failed");
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case "Available": return "bg-green-100 text-green-800";
      case "Occupied": return "bg-red-100 text-red-800";
      case "Dirty": return "bg-yellow-100 text-yellow-800";
      case "Reserved": return "bg-blue-100 text-blue-800";
      case "Maintenance": return "bg-purple-100 text-purple-800";
      default: return "bg-gray-100 text-gray-800";
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#D4AF37]"></div>
      </div>
    );
  }

  return (
    <div className="p-6 sm:p-8">
      {/* Header */}
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="font-heading font-extrabold text-3xl text-[#2D2C2A] flex items-center gap-2">
            <Hotel /> Room Management
          </h1>
          <p className="text-sm text-[#7A7873] mt-1">
            Manage hotel rooms and inventory
          </p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" onClick={loadData}>
            <RefreshCw size={16} className="mr-1.5" /> Refresh
          </Button>
          <Button onClick={openAddDialog} className="bg-[#D4AF37] hover:bg-[#C19B2A]">
            <Plus size={16} className="mr-1.5" /> Add Room
          </Button>
        </div>
      </div>

      {/* Rooms Table */}
      <Card>
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Room Number</TableHead>
              <TableHead>Category</TableHead>
              <TableHead>Floor</TableHead>
              <TableHead>Bed Type</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Smoking</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rooms.length === 0 ? (
              <TableRow>
                <TableCell colSpan={7} className="text-center text-[#7A7873] py-8">
                  No rooms found. Add your first room to get started.
                </TableCell>
              </TableRow>
            ) : (
              rooms.map((room) => (
                <TableRow key={room.id}>
                  <TableCell className="font-semibold">{room.room_number}</TableCell>
                  <TableCell>{room.category_name}</TableCell>
                  <TableCell>{room.floor}</TableCell>
                  <TableCell>{room.bed_type}</TableCell>
                  <TableCell>
                    <Badge className={getStatusColor(room.status)}>
                      {room.status}
                    </Badge>
                  </TableCell>
                  <TableCell>{room.smoking_allowed ? "Yes" : "No"}</TableCell>
                  <TableCell className="text-right">
                    <div className="flex justify-end gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => openEditDialog(room)}
                      >
                        <Pencil size={14} />
                      </Button>
                      <Button
                        size="sm"
                        variant="destructive"
                        onClick={() => handleDelete(room.id)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </div>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </Card>

      {/* Add/Edit Dialog */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>
              {editingRoom ? "Edit Room" : "Add New Room"}
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <Label htmlFor="room_number">Room Number *</Label>
              <Input
                id="room_number"
                value={formData.room_number}
                onChange={(e) => setFormData({ ...formData, room_number: e.target.value.toUpperCase() })}
                placeholder="e.g., G01, 101, 201"
                required
              />
            </div>

            <div>
              <Label htmlFor="category_id">Category *</Label>
              <Select
                value={formData.category_id}
                onValueChange={(value) => setFormData({ ...formData, category_id: value })}
                required
              >
                <SelectTrigger>
                  <SelectValue placeholder="Select category" />
                </SelectTrigger>
                <SelectContent>
                  {categories.map((cat) => (
                    <SelectItem key={cat.id} value={cat.id.toString()}>
                      {cat.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="floor">Floor *</Label>
                <Select
                  value={formData.floor}
                  onValueChange={(value) => setFormData({ ...formData, floor: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Ground">Ground</SelectItem>
                    <SelectItem value="1st Floor">1st Floor</SelectItem>
                    <SelectItem value="2nd Floor">2nd Floor</SelectItem>
                    <SelectItem value="3rd Floor">3rd Floor</SelectItem>
                    <SelectItem value="4th Floor">4th Floor</SelectItem>
                    <SelectItem value="5th Floor">5th Floor</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              <div>
                <Label htmlFor="bed_type">Bed Type *</Label>
                <Select
                  value={formData.bed_type}
                  onValueChange={(value) => setFormData({ ...formData, bed_type: value })}
                >
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="Single">Single</SelectItem>
                    <SelectItem value="Double">Double</SelectItem>
                    <SelectItem value="Queen">Queen</SelectItem>
                    <SelectItem value="King">King</SelectItem>
                    <SelectItem value="Twin">Twin</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label htmlFor="status">Status *</Label>
              <Select
                value={formData.status}
                onValueChange={(value) => setFormData({ ...formData, status: value })}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="Available">Available</SelectItem>
                  <SelectItem value="Occupied">Occupied</SelectItem>
                  <SelectItem value="Dirty">Dirty</SelectItem>
                  <SelectItem value="Reserved">Reserved</SelectItem>
                  <SelectItem value="Maintenance">Maintenance</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="smoking_allowed"
                checked={formData.smoking_allowed}
                onChange={(e) => setFormData({ ...formData, smoking_allowed: e.target.checked })}
                className="w-4 h-4 rounded border-gray-300"
              />
              <Label htmlFor="smoking_allowed" className="cursor-pointer">
                Smoking Allowed
              </Label>
            </div>

            <div className="flex justify-end gap-2 pt-4">
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancel
              </Button>
              <Button type="submit" className="bg-[#D4AF37] hover:bg-[#C19B2A]">
                {editingRoom ? "Update" : "Create"}
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
